import React, { useState } from "react";
import {
  ArrowLeftRight,
  Clock,
  Loader2,
  Lock,
  MoreVertical,
  Package,
  Pencil,
  Receipt,
  RefreshCcw,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import orderService from "@/services/orderService";
import stockService, {
  EXPIRING_CRITICAL_DAYS,
  EXPIRING_DAYS,
} from "@/services/stockService";

/**
 * Situação do lote: disponível (físico - reservado), status pelo mínimo do
 * lote e dias até a validade. Usado no card e nos filtros da tela.
 */
export const getLotInfo = (stock) => {
  const physical = Number(stock?.current_quantity || 0);
  const reserved = Number(stock?.reserved_quantity || 0);
  const available = stockService.calculateAvailableQuantity(physical, reserved);
  const status = stockService.getStockStatus(
    physical,
    reserved,
    stock?.min_quantity
  );
  const level =
    status.variant === "destructive"
      ? "out"
      : status.variant === "warning"
      ? "low"
      : "ok";
  const days = stockService.calculateDaysUntilExpiration(stock?.expiration_date);
  const expiration =
    days === null
      ? "none"
      : days < 0
      ? "expired"
      : days <= EXPIRING_CRITICAL_DAYS
      ? "critical"
      : days <= EXPIRING_DAYS
      ? "soon"
      : "ok";

  const activeReservations = stockService.getActiveReservations(stock);
  // Pedidos finalizados/cancelados não são listados no lote
  const reservations = activeReservations.filter(
    (r) => !stockService.isClosedOrderReservation(r)
  );
  const linkedReserved = activeReservations.reduce(
    (sum, r) => sum + Number(r.reserved_quantity || 0),
    0
  );

  return {
    physical,
    reserved,
    available,
    level,
    label: status.label,
    days,
    expiration,
    reservations,
    linkedReserved,
  };
};

// Pedidos exibidos antes do "ver todos"
const VISIBLE_RESERVATIONS = 3;

const formatOrderDate = (value) => {
  const date = orderService.parseDateTime(value);
  return date
    ? date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;
};

const orderLabel = (r, status) => (
  <>
    <Receipt className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
    <strong className="truncate font-semibold text-foreground group-hover:text-primary group-hover:underline">
      #{r.order_code}
    </strong>
    {status && (
      <span className="whitespace-nowrap rounded-full bg-surface-high px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
        {status}
      </span>
    )}
  </>
);

/**
 * Pedidos em que o lote está reservado. Só lista reservas ainda não
 * confirmadas e de pedidos em aberto; reserva sem pedido (órfã) aparece como
 * aviso. Com `onOpenOrder`, o nº do pedido abre a Gestão de Pedidos.
 */
function LotReservations({ reserved, linkedReserved, reservations, onOpenOrder }) {
  const [expanded, setExpanded] = useState(false);
  if (reservations.length === 0 && reserved <= linkedReserved) return null;

  const visible = expanded
    ? reservations
    : reservations.slice(0, VISIBLE_RESERVATIONS);
  const hidden = reservations.length - visible.length;
  const orphan = reserved - linkedReserved;

  return (
    <div className="rounded-lg border border-terra/20 bg-terra-soft/40 p-2.5">
      {reservations.length > 0 && (
        <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-terra">
          <Lock className="h-3.5 w-3.5" />
          Reservado para{" "}
          {reservations.length === 1
            ? "1 pedido"
            : `${reservations.length} pedidos`}
        </div>
      )}
      {reservations.length > 0 && (
        <ul className="flex flex-col gap-1">
          {visible.map((r) => {
            const when = formatOrderDate(r.order_date);
            const status = orderService.getStatusName(r.order_status);
            return (
              <li
                key={r.id || `${r.order_code}-${r.stock_lot_id}`}
                className="flex items-center justify-between gap-2 text-xs"
              >
                {onOpenOrder ? (
                  <button
                    type="button"
                    onClick={() => onOpenOrder(r.order_code)}
                    title={`Abrir o pedido #${r.order_code} na Gestão de Pedidos`}
                    className="group flex min-w-0 items-center gap-1.5 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {orderLabel(r, status)}
                  </button>
                ) : (
                  <span className="flex min-w-0 items-center gap-1.5">
                    {orderLabel(r, status)}
                  </span>
                )}
                <span className="flex-shrink-0 whitespace-nowrap text-muted-foreground">
                  {when && <span className="mr-1.5">{when}</span>}
                  <strong className="font-semibold text-terra">
                    {r.reserved_quantity} un.
                  </strong>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {reservations.length > VISIBLE_RESERVATIONS && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 text-[11px] font-semibold text-primary hover:underline"
        >
          {expanded ? "Mostrar menos" : `Ver mais ${hidden} pedido${hidden === 1 ? "" : "s"}`}
        </button>
      )}
      {orphan > 0 && (
        <p className="mt-1.5 text-[11px] text-muted-foreground">
          {orphan} un. reservada{orphan === 1 ? "" : "s"} sem pedido vinculado.
          Use "Corrigir reservas do produto" no menu.
        </p>
      )}
    </div>
  );
}

const STATUS_STYLES = {
  ok: "bg-brand-soft text-brand-dark",
  low: "bg-terra-soft text-terra",
  out: "bg-destructive/10 text-destructive",
};

const AVAILABLE_STYLES = {
  ok: "text-primary",
  low: "text-terra",
  out: "text-destructive",
};

const EXPIRATION_STYLES = {
  expired: "bg-destructive text-destructive-foreground",
  critical: "bg-destructive/10 text-destructive",
  soon: "bg-secondary text-secondary-foreground",
  ok: "bg-surface-high text-muted-foreground",
  none: "bg-surface-high text-muted-foreground",
};

const expirationLabel = (days, date) => {
  if (days === null) return "Sem validade";
  const when = stockService.formatDate(date).slice(0, 5);
  if (days < 0) {
    const ago = Math.abs(days);
    return `Vencido há ${ago} dia${ago === 1 ? "" : "s"} (${when})`;
  }
  if (days === 0) return `Vence hoje (${when})`;
  return `${days}d restante${days === 1 ? "" : "s"} (${when})`;
};

/**
 * Card do lote de estoque (docs/tela/code.html): foto, produto, lote/local,
 * preço de venda, faixa de quantidades com status, validade, custo/total e
 * ações (Movimentar, Ajustar e mais opções).
 */
export default function StockLotCard({
  stock,
  imageUrl,
  adjusting = false,
  onMove,
  onEdit,
  onAdjustReservations,
  onDelete,
  onOpenOrder,
}) {
  // Guarda a URL que falhou para cair no ícone sem ficar tentando de novo
  const [failedSrc, setFailedSrc] = useState(null);
  const info = getLotInfo(stock);
  const showImage = imageUrl && failedSrc !== imageUrl;
  const totalValue = stockService.calculateTotalValue(
    info.physical,
    stock.unit_cost
  );
  const name = stock.product_name || "Produto sem nome";

  return (
    <article className="flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-soft transition-shadow hover:shadow-ambient">
      {/* Produto, lote/local e preço de venda */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted text-muted-foreground">
            {showImage ? (
              <img
                src={imageUrl}
                alt={name}
                loading="lazy"
                onError={() => setFailedSrc(imageUrl)}
                className="h-full w-full object-cover"
              />
            ) : (
              <Package className="h-5 w-5" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-baseline gap-1.5">
              <span className="text-[11px] font-semibold text-muted-foreground">
                #{stock.id}
              </span>
              <h3
                className="truncate font-display text-base font-bold leading-tight text-foreground"
                title={name}
              >
                {name}
              </h3>
            </div>
            <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
              <span className="truncate">
                Lote:{" "}
                <strong className="font-medium text-foreground">
                  {stock.lot_code || "-"}
                </strong>
              </span>
              <span aria-hidden="true">•</span>
              <span className="truncate">
                Local:{" "}
                <strong className="font-medium text-foreground">
                  {stock.storage_location_name || "-"}
                </strong>
              </span>
            </div>
          </div>
        </div>
        <span
          className="whitespace-nowrap font-display text-base font-bold text-primary"
          title="Preço de venda"
        >
          {formatCurrency(stock.product_price)}
        </span>
      </div>

      {/* Quantidades e status */}
      <div className="flex items-center justify-between gap-2 rounded-lg bg-surface p-2.5">
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold text-muted-foreground">
            Estoque Físico
          </span>
          <div className="flex items-baseline gap-1">
            <span className="font-display text-sm font-bold text-foreground">
              {info.physical} un.
            </span>
            {info.reserved > 0 && (
              <span
                className="text-[11px] font-medium text-destructive"
                title="Reservadas para pedidos em andamento"
              >
                (-{info.reserved} res.)
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold text-muted-foreground">
            Disponível
          </span>
          <span
            className={cn(
              "font-display text-sm font-bold",
              AVAILABLE_STYLES[info.level]
            )}
          >
            {Math.max(info.available, 0)} un.
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span className="mb-0.5 text-[11px] font-semibold text-muted-foreground">
            Status
          </span>
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[11px] font-semibold",
              STATUS_STYLES[info.level]
            )}
          >
            {info.label}
          </span>
        </div>
      </div>

      <LotReservations
        reserved={info.reserved}
        linkedReserved={info.linkedReserved}
        reservations={info.reservations}
        onOpenOrder={onOpenOrder}
      />

      {/* Validade e valores */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold",
            EXPIRATION_STYLES[info.expiration]
          )}
          title={`Validade: ${stockService.formatDate(stock.expiration_date)}`}
        >
          <Clock className="h-3.5 w-3.5" />
          {expirationLabel(info.days, stock.expiration_date)}
        </span>
        <div className="ml-auto text-right text-xs">
          <span className="text-muted-foreground">
            Custo un.: {formatCurrency(stock.unit_cost)} |{" "}
          </span>
          <span className="font-semibold text-primary">
            Total: {formatCurrency(totalValue)}
          </span>
        </div>
      </div>

      {/* Ações */}
      <div className="flex items-center justify-end gap-2 pt-1">
        {/* modal={false}: o item abre um AlertDialog; com menu modal o Radix
            pode deixar o body travado (pointer-events: none) */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Mais opções do lote ${stock.lot_code || stock.id}`}
              title="Mais opções"
              className="mr-auto flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {adjusting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <MoreVertical className="h-4 w-4" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {stock.product_code && (
              <>
                <DropdownMenuItem
                  disabled={adjusting}
                  onSelect={() => onAdjustReservations(stock)}
                >
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Corrigir reservas do produto
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              onSelect={() => onDelete(stock)}
              className="text-destructive focus:bg-destructive/10 focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Excluir lote
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          onClick={() => onMove(stock)}
          className="flex h-9 items-center gap-1.5 rounded-full bg-surface-high px-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted active:scale-95"
        >
          <ArrowLeftRight className="h-4 w-4" />
          Movimentar
        </button>
        <button
          type="button"
          onClick={() => onEdit(stock)}
          className="flex h-9 items-center gap-1.5 rounded-full bg-primary px-3.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-brand-dark active:scale-95"
        >
          <Pencil className="h-4 w-4" />
          Ajustar
        </button>
      </div>
    </article>
  );
}
